import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetCommodityStates } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodityState } from '../../../../@types/model/masterData/commodityState/commodityState';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import CommodityStateHttpService from '../../../../services/http/masterData/commodityStateHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import { ICommodityStateFormValues } from '../../../../@types/model/masterData/commodityState/commodityStateFormValues';
import CommodityStateForm from './form/CommodityStateForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import { setCommodityStateMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import CommodityStateModelHelper from '../../../../@types/model/masterData/commodityState/commodityStateModelHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';
import UserHelper from '../../../../services/helper/userHelper';

interface ICommodityStateScreenProps {
    selectedCommodity : ICommodity | null;
}

const CommodityStateScreen = (props : ICommodityStateScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const commodityStates = useAppSelector(x => x.masterData.commodityStates);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedCommodityState, setSelectedCommodityState] = useState<ICommodityState | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingCommodityState, setDeletingCommodityState] = useState<ICommodityState | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await CommodityStateHttpService.getCommodityStateData();
                dispatch(dataSetCommodityStates(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading commodity states.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : ICommodityStateFormValues) => {
        setIsLoading(true);

        try {
            const upsert = CommodityStateModelHelper.createUpsert(values);
            const res = await CommodityStateHttpService.addOrUpdateCommodityState(upsert);
            const updatedList = upsertArrayElement(commodityStates, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodityStates(updatedList));
            await setCommodityStateMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedCommodityState ? 'Commodity State updated successfully.' : 'Commodity State added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating commodity state data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeCommodityState = async () => {
        if (!deletingCommodityState) return;
        const updatedCommodityState = { ...deletingCommodityState, isActive: false };
        setIsLoading(true);
        try {
            const res = await CommodityStateHttpService.deleteCommodityState(updatedCommodityState.id);
            const updatedList = upsertArrayElement(commodityStates, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodityStates(updatedList));
            await setCommodityStateMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Commodity State deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting commodity state.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedCommodityState(null);
    };

    const edit = (selectedRow : ICommodityState) => {
        setSelectedCommodityState(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ICommodityState) : void => {
        setDeletingCommodityState(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingCommodityState(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Commodity State', RightGrantsEnum.Add);
        } else if (!props.selectedCommodity) {
            message = 'Commodity Not Selected';
        }

        return message;
    };

    const deleteButtonTooltip = (row : ICommodityState) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Commodity State', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatCommodities = (commodityIds : Array<number>) => {
        let string = commodities
            .filter(x => commodityIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
        if (string.length > 85) {
            string = string.substring(0, 86) + '...';
        }
        return string;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return CommodityStateModelHelper.createFormValues(selectedCommodityState, commodities, props.selectedCommodity);
        }
    }, [selectedCommodityState, isDialogOpen, commodities, props.selectedCommodity]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return props.selectedCommodity
            ? commodityStates.filter(x => x.commodityIds.some(y => y === props.selectedCommodity?.id))
            : commodityStates;
    }, [props.selectedCommodity, commodityStates]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <Card className={'fdc hfill'}>
                <CustomTable<ICommodityState>
                        
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || !props.selectedCommodity || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Commodity Condition', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : ICommodityState) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableSorting
                    enableFiltering
                    enablePagination
                    columns={[
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Commodities', field: 'commodityIds', formatFunction: formatCommodities, width: 300, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    fitWidthToPage
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            { isDialogOpen && initialValues &&
                <ProjectDialog
                    title={`${selectedCommodityState ? 'Edit Commodity State' : 'Add Commodity State'} ${(props.selectedCommodity ? '- (' + props.selectedCommodity.code + ')' : '')}`}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <CommodityStateForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Commodity State'}
                open={!!deletingCommodityState}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeCommodityState}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default CommodityStateScreen;
